'use client';

import { Event } from '@api';
import { useCallback, useMemo, useState } from 'react';
import css from './events.module.scss';

const calendarColumns = ['Date', 'Event', 'Location', 'Focus'];

export const Events = ({ events }: { events: Event[] }) => {
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
        key: 'Date',
        direction: 'desc',
    });

    const sortedItems = useMemo(() => {
        const sorted = [...events].sort((a, b) => {
            const key = sortConfig.key.toLowerCase();
            const compareA = key === 'date' ? new Date(a.startDate) : (a as any)[key];
            const compareB = key === 'date' ? new Date(b.startDate) : (b as any)[key];

            if (compareA < compareB) return sortConfig.direction === 'asc' ? -1 : 1;
            if (compareA > compareB) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });

        return sorted;
    }, [events, sortConfig]);

    const handlerDate = useCallback((event: Event) => {
        const startDate = new Date(event.startDate);
        const endDate = new Date(event.endDate);

        return `${startDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
        })}-${endDate.toLocaleDateString('en-US', {
            day: 'numeric',
        })}`;
    }, []);

    const handleSort = (key: string) => {
        setSortConfig((prevState) => ({
            key,
            direction: prevState.key === key && prevState.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    return (
        <div className={css['event-table']}>
            <table>
                <thead>
                <tr>
                    {calendarColumns.map((column) => (
                        <th key={column} className={column === 'Focus' ? css['hidden-mobile'] : ''}>
                            <div className="flex justify-between items-center">
                                {column}
                                <button
                                    onClick={() => handleSort(column)}
                                    className={`sort-button`}
                                    aria-label={`Sort by ${column}`}
                                >
                                    <svg
                                        width="16"
                                        height="17"
                                        viewBox="0 0 16 17"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M2 3.04492H10.6667M2 5.71159H8M2 8.37825H8M11.3333 5.71159V13.7116M11.3333 13.7116L8.66667 11.0449M11.3333 13.7116L14 11.0449"
                                            stroke={
                                                sortConfig.key === column ? '#3D3D3D' : '#7A756C'
                                            }
                                            strokeWidth={1}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </th>
                    ))}
                </tr>
                </thead>

                <tbody>
                {sortedItems.map((event) => {
                    const dateRange = handlerDate(event);

                    return (
                        <tr key={event.id}>
                            <td>{dateRange}</td>
                            <td>
                                <a href={event.link} target="_blank" rel="noopener noreferrer">
                                    {event.name}
                                </a>
                            </td>
                            <td>{event.location}</td>
                            <td className={css['hidden-mobile']}>{event.focus}</td>
                        </tr>
                    );
                })}
                </tbody>
            </table>
        </div>
    );
};